import { useState } from 'react';
import { User, Phone, MapPin, Save, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const EmployeeProfile = ({ user, setUser }) => {
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    postal_code: user?.postal_code || '',
    city: user?.city || ''
  });
  const [saving, setSaving] = useState(false);

  // Helper to check if a field has a valid value
  const hasValue = (val) => val && typeof val === 'string' && val.trim().length > 0;
  
  // Profile is locked once ALL required fields are filled (including address, postal_code, city)
  const isProfileLocked = hasValue(user?.first_name) && hasValue(user?.last_name) && hasValue(user?.phone) && hasValue(user?.address) && hasValue(user?.postal_code) && hasValue(user?.city);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/auth/profile`, formData, { withCredentials: true });
      
      // Refresh user data
      const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(meRes.data);
      
      alert('✅ Profil mis à jour avec succès !');
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('❌ Erreur lors de la mise à jour: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const isProfileComplete = () => {
    const hasVal = (val) => val && typeof val === 'string' && val.trim().length > 0;
    return hasVal(formData.first_name) && 
           hasVal(formData.last_name) && 
           hasVal(formData.phone) && 
           hasVal(formData.address) &&
           hasVal(formData.postal_code) &&
           hasVal(formData.city);
  };

  // Check if profile is complete with all required fields (use the hasValue already defined above)
  const isProfileIncomplete = !hasValue(user?.first_name) || !hasValue(user?.last_name) || !hasValue(user?.phone) || !hasValue(user?.address) || !hasValue(user?.postal_code) || !hasValue(user?.city) || !hasValue(user?.email);
  
  // Get list of missing fields
  const getMissingFields = () => {
    const missing = [];
    if (!formData.first_name) missing.push('Prénom');
    if (!formData.last_name) missing.push('Nom');
    if (!formData.phone) missing.push('Téléphone');
    if (!formData.address) missing.push('Adresse');
    if (!formData.postal_code) missing.push('Code postal');
    if (!formData.city) missing.push('Ville');
    if (!user?.email) missing.push('Email');
    return missing;
  };

  return (
    <Layout user={user}>
      <div className="space-y-6 fade-in max-w-2xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] font-['Chivo']">Mon Profil</h1>
          <p className="text-gray-500">Gérez vos informations personnelles</p>
        </div>

        {/* Alert if profile is locked */}
        {isProfileLocked && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-green-700">Profil complet ✓</p>
                  <p className="text-sm text-green-600">Vos informations ont été enregistrées. Pour toute modification, contactez l&apos;administration.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alert if profile incomplete - BLOCKING */}
        {isProfileIncomplete && !isProfileLocked && (
          <Card className="border-red-300 bg-red-50">
            <CardContent className="p-4">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-red-700">⚠️ Profil incomplet - Accès limité</p>
                    <p className="text-sm text-red-600">
                      Vous devez compléter TOUTES les informations ci-dessous pour accéder au tableau de bord.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 ml-13">
                  <p className="text-sm font-medium text-gray-700 mb-2">Informations manquantes :</p>
                  <ul className="space-y-1">
                    {getMissingFields().map((field, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-red-600">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        {field}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personal Information Form */}
        <Card>
          <CardHeader>
            <CardTitle>Informations personnelles</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email (readonly) */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">L&apos;email ne peut pas être modifié</p>
              </div>

              {/* First Name */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4" />
                  Prénom <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="Votre prénom"
                  disabled={isProfileLocked}
                  required
                />
              </div>

              {/* Last Name */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4" />
                  Nom de famille <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Votre nom de famille"
                  disabled={isProfileLocked}
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4" />
                  Téléphone <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="06 12 34 56 78"
                  disabled={isProfileLocked}
                  required
                />
              </div>

              {/* Address */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4" />
                  Adresse <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Numéro et nom de rue"
                  disabled={isProfileLocked}
                  required
                />
              </div>

              {/* Postal Code and City */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4" />
                    Code postal <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                    placeholder="75001"
                    disabled={isProfileLocked}
                    required
                    maxLength={5}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4" />
                    Ville <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Paris"
                    disabled={isProfileLocked}
                    required
                  />
                </div>
              </div>

              {!isProfileLocked && (
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full btn-accent h-12"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      Enregistrer les modifications
                    </>
                  )}
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Site Information (readonly) */}
        {user?.site_name && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Site affecté</p>
                  <p className="font-bold text-blue-700">{user.site_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default EmployeeProfile;
